// Utility per determinare l'origin del backend, adattandosi a scenari locali e remoti
export function getBackendBaseOrigin(): string {
    const envVars = process.env as Record<string, string | undefined>;
    const configuredApiUrl = envVars['REACT_APP_API_URL'];
    const hostname = window.location.hostname;
    const isLoopbackHost = ['localhost', '127.0.0.1', '::1'].includes(hostname);
    const isDev = process.env.NODE_ENV !== 'production';

    const forceLocal = envVars['REACT_APP_USE_LOCAL_BACKEND'] === '1';
    const devForceLocal = envVars['REACT_APP_DEV_FORCE_LOCAL'] === '1';

    const parseCsvSet = (value?: string): Set<string> => {
        if (!value) return new Set();
        return new Set(
            value
                .split(',')
                .map(token => token.trim())
                .filter(token => token.length > 0)
        );
    };

    const devLocalTenantTokens = parseCsvSet(envVars['REACT_APP_DEV_LOCAL_TENANTS']);

    const shouldPreferLocalForTenant = () => {
        if (!isDev || devLocalTenantTokens.size === 0 || typeof window === 'undefined') {
            return false;
        }

        try {
            const candidates: string[] = [];
            const { localStorage } = window;
            const pushCandidate = (value?: string | null) => {
                const trimmed = (value ?? '').trim();
                if (trimmed) candidates.push(trimmed);
            };

            pushCandidate(localStorage?.getItem('tenantId'));
            pushCandidate(localStorage?.getItem('tenantSlug'));

            const params = new URLSearchParams(window.location.search);
            pushCandidate(params.get('tenantid'));
            pushCandidate(params.get('tenantId'));

            const pathMatch = window.location.pathname.match(/\/tenant\/([^/?#]+)/i);
            if (pathMatch && pathMatch[1]) {
                pushCandidate(pathMatch[1]);
            }

            if (!isLoopbackHost) {
                const subdomain = hostname.split('.')[0];
                pushCandidate(subdomain);
            }

            return candidates.some(candidate => devLocalTenantTokens.has(candidate));
        } catch {
            return false;
        }
    };

    const resolveLocalOrigin = () => {
        const explicitLocalUrl = envVars['REACT_APP_LOCAL_BACKEND_URL'];
        if (explicitLocalUrl) {
            return explicitLocalUrl;
        }
        const devPort = envVars['REACT_APP_DEV_API_PORT'];
        if (devPort) {
            return `http://localhost:${devPort}`;
        }
        return 'http://localhost:8080';
    };

    if (forceLocal || (isDev && devForceLocal) || shouldPreferLocalForTenant()) {
        return resolveLocalOrigin();
    }

    if (configuredApiUrl) {
        try {
            const parsed = new URL(configuredApiUrl);
            const hasWindow = typeof window !== 'undefined' && window.location?.origin;
            const sameHost = parsed.hostname === hostname;

            if (!isDev && hasWindow && hostname && !isLoopbackHost && !sameHost) {
                // In produzione preferisci l'origin del tenant quando l'host non combacia
                return window.location.origin;
            }

            if (isDev && hostname && !isLoopbackHost) {
                const port = parsed.port ? `:${parsed.port}` : '';
                return `${parsed.protocol}//${hostname}${port}`;
            }

            return parsed.origin;
        } catch {
            if (forceLocal || (isDev && devForceLocal) || shouldPreferLocalForTenant()) {
                return resolveLocalOrigin();
            }
            if (isDev && hostname && !isLoopbackHost) {
                return `http://${hostname}:8080`;
            }
            if (typeof window !== 'undefined' && window.location?.origin) {
                return window.location.origin;
            }
            return configuredApiUrl;
        }
    }

    if (isDev) {
        return resolveLocalOrigin();
    }

    if (typeof window !== 'undefined' && window.location?.origin) {
        return window.location.origin;
    }

    return 'https://app.qvadimenu.com';
}
